import React, { useEffect } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WelcomeWrapper from "./Welcome.style";
import { RegistrationLayout } from "../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../RegistrationLayout/RegistrationSetupNavbar";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
  appleUrl,
  googleUrl,
} from "../../helper/constants";
import NutritionLogo from "../../assets/images/nutritionlogo.png";
import {
  clearRegistrationData,
  isRegistrationComplete,
} from "../RegistrationSetup/registrationSetupStorage";
import { trackRegistrationStarted } from "../../helper/registrationAnalytics";

const AppleStoreIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="white"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const GooglePlayIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3.18 23.44c-.3-.17-.48-.49-.48-.91V1.47c0-.42.18-.74.48-.91l.08-.04 12.28 12.28v.3L3.26 23.48l-.08-.04z"
      fill="#EA4335"
    />
    <path
      d="M19.69 16.08l-4.23-4.24v-.3l4.23-4.23.09.05 5.01 2.85c1.43.81 1.43 2.14 0 2.96l-5.01 2.85-.09.06z"
      fill="#FBBC04"
    />
    <path
      d="M3.26 23.48 15.54 11.2 19.78 15.44 5.04 23.94c-.49.28-1.01.3-1.78-.46z"
      fill="#34A853"
    />
    <path
      d="M3.26.52C4.03-.24 4.55-.22 5.04.06l14.74 8.5-4.24 4.24L3.26.52z"
      fill="#4285F4"
    />
  </svg>
);

const WELCOME_STEP = REGISTRATION_SETUP_STEP_META.WELCOME;

const WELCOME_FEATURES = [
  {
    icon: "🥗",
    titleKey: "NUTRITION_INTRO_LBL_FEATURE1_TITLE",
    descKey: "NUTRITION_INTRO_DESC_FEATURE1",
  },
  {
    icon: "💳",
    titleKey: "NUTRITION_INTRO_LBL_FEATURE2_TITLE",
    descKey: "NUTRITION_INTRO_DESC_FEATURE2",
  },
  {
    icon: "📅",
    titleKey: "NUTRITION_INTRO_LBL_FEATURE3_TITLE",
    descKey: "NUTRITION_INTRO_DESC_FEATURE3",
  },
];

const Welcome = ({ history, location }) => {
  const { t } = useTranslation();
  const search = location?.search || "";
  const codeFromQuery = new URLSearchParams(search).get("code");
  const shouldBypassWelcome =
    typeof codeFromQuery === "string" && codeFromQuery.trim().length > 0;

  useEffect(() => {
    if (isRegistrationComplete()) {
      clearRegistrationData();
    }

    // Deep links with ?code= skip welcome and go straight to account setup
    if (shouldBypassWelcome) {
      trackRegistrationStarted();
      history.replace(`/register/account${search}`);
      return undefined;
    }

    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history, search, shouldBypassWelcome]);

  const handleGetStarted = () => {
    trackRegistrationStarted();
    history.push(`/register/account${search}`);
  };

  if (shouldBypassWelcome) {
    return null;
  }

  return (
    <RegistrationLayout
      containScroll
      header={
        <RegistrationSetupNavbar
          variant="welcome"
          stepMeta={WELCOME_STEP}
          totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
          showBack={false}
        />
      }
    >
      <WelcomeWrapper>
        <div className="scroll-body">
            <div className="content-card">
              <div className="content-card-scroll">
              <div className="hero-section">
                <div className="hero-icon-wrap">
                  <img
                    src={NutritionLogo}
                    alt={t("CM_LBL_HUSK_NUTRITION")}
                    className="hero-icon"
                  />
                </div>
                <div className="section-eyebrow">{t("NUTRITION_WELCOME_LBL_EYEBROW")}</div>
                <h1 className="section-title">
                  {t("NUTRITION_INTRO_LBL_WELCOME_TO")}
                  <br />
                  <em>{t("CM_LBL_HUSK_NUTRITION")}</em>
                </h1>
                <p className="section-sub">
                  {t("NUTRITION_INTRO_DESC_SUBTITLE")}
                </p>
              </div>

              <div className="feature-list">
                {WELCOME_FEATURES.map((feature) => (
                  <div className="feature-item" key={feature.titleKey}>
                    <div className="feature-icon">{feature.icon}</div>
                    <div>
                      <div className="feature-title">{t(feature.titleKey)}</div>
                      {feature.descKey ? (
                        <div className="feature-desc">{t(feature.descKey)}</div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              <div className="notice-box notice-box-privacy">
                <div className="notice-icon">🔒</div>
                <div className="notice-text">
                  {t("NUTRITION_INTRO_DESC_HIPAA_NOTICE")}
                </div>
              </div>

              <button
                type="button"
                className="get-started-btn"
                onClick={handleGetStarted}
              >
                {t("GS_BTN_GET_STARTED")} →
              </button>

              <div className="footer-section">
                <p className="footer-text">
                  {t("NUTRITION_WELCOME_DESC_ALREADY_HAVE_ACCOUNT")}
                </p>
                <div className="app-icon">
                  <a
                    href={appleUrl}
                    className="store-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppleStoreIcon />
                    <div className="store-btn-label">
                      <div className="store-btn-eyebrow">Download on the</div>
                      <div className="store-btn-title">
                        {t("NUTRITION_WELCOME_LBL_APP_STORE_LINE2")}
                      </div>
                    </div>
                  </a>
                  <a
                    href={googleUrl}
                    className="store-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GooglePlayIcon />
                    <div className="store-btn-label">
                      <div className="store-btn-eyebrow">
                        {t("NUTRITION_WELCOME_LBL_GOOGLE_PLAY_LINE1")}
                      </div>
                      <div className="store-btn-title">
                        {t("NUTRITION_WELCOME_LBL_GOOGLE_PLAY_LINE2")}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
              </div>
            </div>
        </div>
      </WelcomeWrapper>
    </RegistrationLayout>
  );
};

export default withRouter(Welcome);
